import { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import axios from 'axios';
import { Edit2, Trash2, Reply, Check, CheckCheck, Pin, PinOff, Forward, Eye, BellOff, Bookmark, Copy, CheckSquare, Languages } from 'lucide-react';
import clsx from 'clsx';
import { Message } from '@/types';
import Avatar from '@/components/ui/Avatar';
import { useAuthStore } from '@/store/authStore';
import { messagesApi } from '@/api/messages';
import { apiClient } from '@/api/client';
import { useChatStore } from '@/store/chatStore';
import { useUiPrefsStore } from '@/store/uiPrefsStore';
import { useStickerCacheStore } from '@/store/stickerCacheStore';
import { useSelectionStore, type SelectionScope } from '@/store/selectionStore';
import { useChannelRole } from '@/hooks/useChannelRole';
import { foldersApi } from '@/api/folders';
import UserContextMenu from './UserContextMenu';
import MessageReactions from './MessageReactions';
import { useKarmaStore } from '@/store/karmaStore';
import { reactionsApi } from '@/api/reactions';
import { apiClient } from '@/api/client';
import ForwardModal from './ForwardModal';
import InlineKeyboard from './InlineKeyboard';
import ImageLightbox from '@/components/ui/ImageLightbox';
import VoiceTranscript from './VoiceTranscript';
import NickName from '@/components/ui/NickName';
import { useSignedMediaUrl } from '@/hooks/useSignedMediaUrl';

interface MessageItemProps {
  message: Message;
  previousMessage?: Message;
  onReply?: (message: Message) => void;
  /**
   * Phase 8.1 — подсветка сообщения после клика по «ответ на …» в другом
   * сообщении или после прыжка из поиска. Управляется родителем
   * (MessageList).
   */
  highlight?: boolean;
  /**
   * Phase 8.1 — клик по предпросмотру «ответ на …». Передаётся в
   * MessageList, который скроллит к нужному сообщению.
   */
  onJumpTo?: (messageId: string) => void;
  /**
   * Phase 11.3 — selection scope of the surrounding view. Required for
   * multi-select to know which channel/DM the message belongs to (DMs
   * carry an empty channelId, so we can't infer scope from the message).
   */
  selectionScope?: SelectionScope;
  /** Phase 20 — IRC classic per-channel rendering. */
  chatStyle?: 'BUBBLE' | 'CLASSIC';
  /**
   * Phase 12.3 — embed iframe режим (russnt.ru). В контекстном меню оставляем
   * только реакции и «Ответить»; убираем «Переслать», «Копировать»,
   * «В Сохранённые», «Закрепить», «Редактировать», «Удалить», «Профиль».
   */
  embedMode?: boolean;
}

function isGrouped(msg: Message, prev?: Message): boolean {
  if (!prev) return false;
  if (msg.authorId !== prev.authorId) return false;
  const diff = new Date(msg.createdAt).getTime() - new Date(prev.createdAt).getTime();
  return diff < 5 * 60 * 1000;
}

// Named color palette for different users
const USER_COLORS = [
  'text-red-400', 'text-orange-400', 'text-yellow-400', 'text-green-400',
  'text-teal-400', 'text-cyan-400', 'text-blue-400', 'text-violet-400', 'text-pink-400',
];

function getUserColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) hash = (hash * 31 + userId.charCodeAt(i)) | 0;
  return USER_COLORS[Math.abs(hash) % USER_COLORS.length];
}

function MessageItem({ message, previousMessage, onReply, highlight, onJumpTo, selectionScope, chatStyle, embedMode }: MessageItemProps) {
  const { user } = useAuthStore();
  const { updateChannelMessage, removeChannelMessage, removeDmMessage } = useChatStore();
  const selActive = useSelectionStore((s) => s.active);
  const selScope = useSelectionStore((s) => s.scope);
  const selIds = useSelectionStore((s) => s.ids);
  const enterSel = useSelectionStore((s) => s.enter);
  const toggleSel = useSelectionStore((s) => s.toggle);

  // This message's surroundings match the active selection scope?
  const matchesScope: boolean = (() => {
    if (!selActive || !selScope || !selectionScope) return false;
    if (selScope.kind !== selectionScope.kind) return false;
    if (selScope.kind === 'channel' && selectionScope.kind === 'channel') {
      return selScope.channelId === selectionScope.channelId;
    }
    if (selScope.kind === 'dm' && selectionScope.kind === 'dm') {
      return selScope.userId === selectionScope.userId;
    }
    return false;
  })();
  const isSelected = matchesScope && selIds.has(message.id);
  const inSelectionMode = matchesScope;
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content);
  const [showActions, setShowActions] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  // Phase 8.1 — отдельный «message actions» контекст: edit/delete/reply/etc.
  // Открывается правым кликом, в т.ч. на собственных сообщениях.
  // Теперь — boolean (анкорится к bubble через ref), а не координаты клика,
  // чтобы меню вставало под/над bubble и не уезжало за viewport.
  const [actionsMenu, setActionsMenu] = useState<boolean>(false);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuPos, setMenuPos] = useState<{ left: number; top: number; visibility: 'hidden' | 'visible' }>({ left: 0, top: 0, visibility: 'hidden' });
  const [forwardOpen, setForwardOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [viewOnceRevealed, setViewOnceRevealed] = useState<string | null>(null);
  const role = useChannelRole(message.channelId || null);
  const isMod = role === 'OWNER' || role === 'ADMIN' || role === 'MODERATOR';
  const isDM = !message.channelId;

  // P1 — signed media URL вместо JWT-в-query. ХУК выдаёт 1 URL за TTL 5 мин,
  // React Query кеширует, повторные мount'ы не дёргают сервер. Вычисляем
  // нужный тип ОДИН раз тут (для основного media-attachment этой message),
  // полная версия (для лайтбокса) запрашивается отдельно при click.
  const mediaUrlType: 'thumbnail' | 'download' =
    message.type === 'IMAGE' && !isDM ? 'thumbnail' : 'download';
  const signedMediaSrc = useSignedMediaUrl(message.mediaFileId ?? null, mediaUrlType);
  const joinedChannels = useChatStore((s) => s.joinedChannels);
  const reactionsEnabled = isDM ? true : (joinedChannels.find((c) => c.id === message.channelId)?.reactionsEnabled ?? true);

  const grouped = isGrouped(message, previousMessage);
  const isOwn = message.authorId === user?.id;
  const isDeleted = !!message.deletedAt;
  const isPinned = !!message.pinnedAt;
  const isForwarded = !!message.forwardFromMessageId;
  const isViewOnce = !!message.viewOnce;
  const viewOnceConsumed = isViewOnce && !!message.viewedAt && !isOwn && viewOnceRevealed !== message.id;

  const handleEdit = async () => {
    if (editContent.trim() === message.content) { setEditing(false); return; }
    try {
      const updated = await messagesApi.editMessage(message.id, editContent.trim());
      updateChannelMessage(updated);
      setEditing(false);
    } catch { /* ignore */ }
  };

  const handleDelete = async () => {
    if (!confirm('Удалить это сообщение?')) return;
    try {
      await messagesApi.deleteMessage(message.id);
      removeChannelMessage(message.id, message.channelId);
    } catch { /* ignore */ }
  };

  // Phase 16 — DM-specific удаление: scope 'me' (локально) / 'all' (у всех).
  // Для чужого сообщения доступен только 'me'.
  const handleDeleteDm = async (scope: 'me' | 'all') => {
    const msg = scope === 'all'
      ? 'Удалить это сообщение у всех?'
      : 'Удалить это сообщение только у вас?';
    if (!confirm(msg)) return;
    try {
      await messagesApi.deleteDm(message.id, scope);
      // Локально убираем сразу (socket подтвердит если нужно).
      // chatStore.removeDmMessage есть? — если нет, dispatch'им через обновление списка.
      // Проще: полагаемся на invalidate через socket в chatStore.
      // На всякий случай — clearDmChat NOT, только удаляем конкретное.
      removeDmMessage(message.id);
    } catch { /* ignore */ }
  };

  const handleTogglePin = async () => {
    try {
      const updated = isPinned
        ? await messagesApi.unpinMessage(message.id)
        : await messagesApi.pinMessage(message.id);
      updateChannelMessage({ ...message, pinnedAt: updated.pinnedAt ?? null, pinnedById: updated.pinnedById ?? null });
    } catch { /* ignore */ }
  };

  const handleSave = async () => {
    try {
      await foldersApi.save(isDM ? { dmId: message.id } : { messageId: message.id });
    } catch { /* ignore */ }
  };

  const handleSaveToPersonal = async () => {
    try {
      await apiClient.post('/api/messages/save-to-personal',
        isDM ? { dmId: message.id } : { messageId: message.id });
    } catch (e) { console.warn('save-to-personal failed', e); }
  };

  const handleRevealViewOnce = async () => {
    try {
      const updated = await messagesApi.consumeViewOnce(message.id);
      setViewOnceRevealed(message.id);
      updateChannelMessage({ ...message, content: updated.content, viewedAt: new Date().toISOString() });
    } catch { /* ignore */ }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDeleted) return;
    if (inSelectionMode) { toggleSel(message.id); return; }
    setActionsMenu(true);
  };

  // Telegram-style gesture model:
  //   - Desktop:  right-click → menu. Left-drag selects text natively.
  //   - Touch:    long press → menu. No native conflict.
  // We don't use mouse-down long-press anymore because it stole text
  // selection on desktop (timer fired mid-drag and replaced the selection
  // with a context menu).
  const pressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartXY = useRef<{ x: number; y: number } | null>(null);
  // v4.3 — grace period после long-press: overlay получает `pointer-events: none`
  // первые 500мс после открытия. Это надёжнее чем ref-based onClick check —
  // synthetic click от touchend (iOS до ~400ms delay) физически не попадает в
  // overlay'ный hit-test. Через 500ms — re-render, pointer-events: auto.
  // Используем state (не ref) чтобы re-render случился.
  const [menuOpenedAt, setMenuOpenedAt] = useState<number | null>(null);
  const inGrace = menuOpenedAt !== null && Date.now() - menuOpenedAt < 500;

  const handleClickRow = (e: React.MouseEvent) => {
    if (!inSelectionMode || isDeleted) return;
    // Don't toggle when the user clicked an interactive child (button, link, input)
    const target = e.target as HTMLElement;
    if (target.closest('button, a, input, textarea, [data-no-select]')) return;
    // Don't toggle if the user is selecting text — let nature take its course
    if ((window.getSelection()?.toString().length ?? 0) > 0) return;
    toggleSel(message.id);
  };
  const handleTouchStart = (e: React.TouchEvent) => {
    if (isDeleted) return;
    if (inSelectionMode) return;
    const t = e.touches[0];
    touchStartXY.current = { x: t.clientX, y: t.clientY };
    pressTimerRef.current = setTimeout(() => {
      // Don't open menu if the user already selected text (double-tap
      // word-select on iOS can fire before this timer)
      if ((window.getSelection()?.toString().length ?? 0) > 0) return;
      setActionsMenu(true);
      // Grace period: overlay не принимает клики первые 500ms (state-based,
      // вынуждает re-render когда grace истечёт). Покрывает iOS click-delay
      // (до 400ms) после touchend.
      setMenuOpenedAt(Date.now());
      setTimeout(() => setMenuOpenedAt(null), 500);
    }, 500);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!pressTimerRef.current || !touchStartXY.current) return;
    const t = e.touches[0];
    const dx = Math.abs(t.clientX - touchStartXY.current.x);
    const dy = Math.abs(t.clientY - touchStartXY.current.y);
    // Tiny jitter is fine. Real movement = the user is scrolling or
    // selecting text — cancel the long-press timer.
    if (dx > 10 || dy > 10) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  };
  const handleTouchEnd = () => {
    if (pressTimerRef.current) { clearTimeout(pressTimerRef.current); pressTimerRef.current = null; }
    touchStartXY.current = null;
  };

  // After the menu mounts, measure its real size and place it relative to the
  // bubble (Telegram-style): below if there's room, otherwise above.
  // Horizontally aligned with the bubble's near edge (right for own, left for
  // others), clamped to the viewport with an 8 px margin.
  useEffect(() => {
    if (!actionsMenu) { setMenuPos((p) => ({ ...p, visibility: 'hidden' })); return; }
    const place = () => {
      const anchor = rowRef.current?.getBoundingClientRect();
      const menu = menuRef.current?.getBoundingClientRect();
      if (!anchor || !menu) return;
      const margin = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // Vertical: prefer below the bubble, flip above if no room
      let top = anchor.bottom + margin;
      if (top + menu.height > vh - margin) {
        const above = anchor.top - menu.height - margin;
        top = above >= margin ? above : Math.max(margin, vh - menu.height - margin);
      }
      // Horizontal: align with bubble's near edge
      let left: number;
      if (isOwn) left = anchor.right - menu.width;
      else left = anchor.left;
      left = Math.max(margin, Math.min(left, vw - menu.width - margin));
      setMenuPos({ left, top, visibility: 'visible' });
    };
    // First paint: render hidden so we can measure; then place on next frame.
    setMenuPos({ left: 0, top: 0, visibility: 'hidden' });
    const raf = requestAnimationFrame(place);
    return () => cancelAnimationFrame(raf);
  }, [actionsMenu, isOwn]);

  // Enter selection mode from the action menu.
  const handleEnterSelection = () => {
    if (!selectionScope) return;
    enterSel(selectionScope, message.id);
    setActionsMenu(false);
  };

  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(message.content); } catch { /* ignore */ }
  };

  const handleUsernameClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Navigate to profile page
    window.location.href = `/u/${message.author.username}`;
  };

  // v5.4 — SYSTEM messages (IRC join/leave/kick/MOTD). Renders as a centered
  // gray "* user joined" line in BOTH BUBBLE and CLASSIC styles. Content is
  // a JSON envelope { kind:'system', code, actorName, text, ... } — мы
  // распарсиваем и показываем text.
  if (message.type === 'SYSTEM') {
    let display = message.content || '';
    let badge: string | null = null;
    try {
      const parsed = JSON.parse(message.content || '{}');
      if (parsed && parsed.kind === 'system' && typeof parsed.text === 'string') {
        display = parsed.text;
        if (parsed.code === 'MOTD') badge = 'MOTD';
      }
    } catch { /* legacy plain-text system message — leave as is */ }
    return (
      <div className="flex justify-center px-4 my-2">
        <div className="text-surface-500 text-xs italic flex items-center gap-2 max-w-[80%] text-center">
          {badge && (
            <span className="inline-block bg-brand-700/30 text-brand-300 px-1.5 py-0.5 rounded text-[10px] font-semibold not-italic">
              {badge}
            </span>
          )}
          <span>{display}</span>
        </div>
      </div>
    );
  }

  if (chatStyle === 'CLASSIC') {
    return (
      <ClassicMessageLine
        message={message}
        isOwn={isOwn}
        isDeleted={isDeleted}
        myUsername={user?.username || ''}
        highlight={!!highlight}
        selectionScope={selectionScope}
      />
    );
  }

  return (
    <>
      <div
        ref={rowRef}
        data-message-id={message.id}
        className={clsx(
          'flex items-end gap-1.5 px-4 transition-colors duration-500',
          isOwn ? 'flex-row-reverse' : 'flex-row',
          grouped ? 'mt-0.5' : 'mt-3',
          highlight && 'bg-brand-500/15 rounded-md',
          isSelected && 'bg-brand-500/10',
          inSelectionMode && 'cursor-pointer',
        )}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
        onContextMenu={handleContextMenu}
        onClick={handleClickRow}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Selection checkbox (in selection mode only) */}
        {inSelectionMode && (
          <div className="w-5 h-5 flex-shrink-0 self-center flex items-center justify-center" data-no-select>
            <div
              className={clsx(
                'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors',
                isSelected ? 'bg-brand-500 border-brand-500' : 'border-surface-500 bg-transparent',
              )}
            >
              {isSelected && <Check size={12} className="text-white" />}
            </div>
          </div>
        )}

        {/* Avatar (others only, hide for grouped) */}
        {!isOwn && (
          <div className={clsx('w-7 h-7 flex-shrink-0 self-end', grouped && 'invisible')}>
            <button onClick={handleUsernameClick} className="cursor-pointer">
              <Avatar src={message.author.avatarUrl} username={message.author.username} size="xs" />
            </button>
          </div>
        )}

        {/* Bubble */}
        <div className={clsx('relative flex flex-col max-w-[75%] md:max-w-[65%]', isOwn ? 'items-end' : 'items-start')}>

          {/* Hover toolbar disabled — all actions moved to context menu (right-click / long press) */}
          {false && showActions && !editing && !isDeleted && (
            <div className={clsx(
              'absolute -top-8 flex items-center gap-0.5 bg-surface-800 border border-surface-700 rounded-xl px-1.5 py-1 shadow-xl z-10',
              isOwn ? 'right-0' : 'left-0',
            )}>
              {isOwn && (
                <>
                  <button
                    onClick={() => { setEditing(true); setEditContent(message.content); }}
                    className="p-1.5 rounded-lg text-surface-400 hover:text-surface-100 hover:bg-surface-700 transition-colors"
                    title="Редактировать"
                  >
                    <Edit2 size={13} />
                  </button>
                  <button
                    onClick={handleDelete}
                    className="p-1.5 rounded-lg text-surface-400 hover:text-red-400 hover:bg-surface-700 transition-colors"
                    title="Удалить"
                  >
                    <Trash2 size={13} />
                  </button>
                </>
              )}
              <button
                onClick={() => onReply?.(message)}
                className="p-1.5 rounded-lg text-surface-400 hover:text-surface-100 hover:bg-surface-700 transition-colors"
                title="Ответить"
              >
                <Reply size={13} />
              </button>
              <button
                onClick={() => setForwardOpen(true)}
                className="p-1.5 rounded-lg text-surface-400 hover:text-surface-100 hover:bg-surface-700 transition-colors"
                title="Переслать"
              >
                <Forward size={13} />
              </button>
              <button
                onClick={handleSave}
                className="p-1.5 rounded-lg text-surface-400 hover:text-yellow-300 hover:bg-surface-700 transition-colors"
                title="Сохранить"
              >
                <Bookmark size={13} />
              </button>
              {!isDM && isMod && (
                <button
                  onClick={handleTogglePin}
                  className="p-1.5 rounded-lg text-surface-400 hover:text-brand-400 hover:bg-surface-700 transition-colors"
                  title={isPinned ? 'Открепить' : 'Закрепить'}
                >
                  {isPinned ? <PinOff size={13} /> : <Pin size={13} />}
                </button>
              )}
            </div>
          )}

          {/* Message bubble */}
          {isDeleted ? (
            <div className="px-3 py-1.5 rounded-2xl bg-surface-800 border border-surface-700">
              <p className="text-surface-500 text-sm italic">Сообщение удалено</p>
            </div>
          ) : editing ? (
            <div className={clsx(
              'rounded-2xl border px-3 py-2 min-w-[220px]',
              isOwn ? 'bg-brand-700 border-brand-600' : 'bg-surface-800 border-surface-700',
            )}>
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleEdit(); }
                  if (e.key === 'Escape') setEditing(false);
                }}
                className="bg-transparent text-surface-100 text-sm w-full outline-none resize-none min-h-[48px]"
                autoFocus
              />
              <div className="flex gap-1.5 mt-2">
                <button onClick={handleEdit} className="btn-primary text-xs px-3 py-1">Сохранить</button>
                <button onClick={() => setEditing(false)} className="btn-ghost text-xs px-3 py-1">Отмена</button>
              </div>
            </div>
          ) : (
            <div className={clsx(
              'relative px-3 py-2 shadow-sm',
              isOwn
                ? 'bg-brand-600 text-white rounded-2xl rounded-br-md'
                : 'bg-surface-800 text-surface-100 rounded-2xl rounded-bl-md',
              // Remove tail rounding for grouped
              grouped && isOwn && 'rounded-br-2xl',
              grouped && !isOwn && 'rounded-bl-2xl',
            )}>
              {/* Sender name (non-own, first in group) */}
              {!isOwn && !grouped && (
                <button
                  onClick={handleUsernameClick}
                  className="text-xs font-semibold mb-0.5 leading-tight hover:underline"
                  title={(() => {
                    const k = useKarmaStore.getState().get(message.authorId);
                    return k !== null ? `★ ${k.toFixed(1)}` : undefined;
                  })()}
                >
                  <NickName
                    user={message.author as any}
                    className={clsx('text-xs font-semibold', (message.author as any).nickColor ? '' : getUserColor(message.authorId))}
                  />
                </button>
              )}

              {/* Forwarded badge */}
              {isForwarded && (
                <div className={clsx('flex items-center gap-1 text-[10px] mb-1 italic', isOwn ? 'text-white/70' : 'text-surface-500')}>
                  <Forward size={10} /> Переслано
                </div>
              )}

              {/* Reply-to */}
              {message.replyTo && (
                <button
                  type="button"
                  onClick={() => onJumpTo?.(message.replyTo!.id)}
                  className={clsx(
                    'flex gap-1.5 text-xs mb-1.5 pl-2 border-l-2 rounded-sm text-left hover:opacity-80 transition-opacity w-full',
                    isOwn ? 'border-white/50 text-white/70' : 'border-brand-400 text-surface-400',
                  )}>
                  <div className="min-w-0">
                    <span className={clsx('font-semibold block', isOwn ? 'text-white/90' : 'text-brand-400')}>
                      <NickName user={message.replyTo.author} />
                    </span>
                    <span className="truncate block max-w-[200px]">{message.replyTo.content}</span>
                  </div>
                </button>
              )}

              {/* Content */}
              {isViewOnce && viewOnceConsumed ? (
                <p className="text-sm italic text-surface-400">Сообщение просмотрено</p>
              ) : isViewOnce && !isOwn && viewOnceRevealed !== message.id ? (
                <button
                  onClick={handleRevealViewOnce}
                  className="flex items-center gap-2 px-3 py-2 bg-black/40 rounded-lg text-white text-sm"
                >
                  <Eye size={14} /> Показать (один раз)
                </button>
              ) : message.type === 'STICKER' ? (
                (() => {
                  const raw = message.content || message.fileUrl || '';
                  const apiBase = ((import.meta as any).env?.VITE_API_URL ?? '').replace(/\/$/, '');
                  const src = /^https?:\/\//i.test(raw) ? raw : raw.startsWith('/') ? `${apiBase}${raw}` : raw;
                  return (
                    <img
                      src={src}
                      alt="Стикер"
                      className="w-[140px] h-[140px] object-contain bg-transparent"
                      loading="lazy"
                    />
                  );
                })()
              ) : message.type === 'IMAGE' && message.mediaFileId ? (
                signedMediaSrc ? (
                  <img
                    src={signedMediaSrc}
                    alt="Изображение"
                    className="rounded-lg max-w-full max-h-[300px] object-cover cursor-pointer"
                    loading="lazy"
                    onClick={() => setLightboxOpen(true)}
                  />
                ) : (
                  <div className="rounded-lg w-[200px] h-[150px] bg-surface-800 animate-pulse" />
                )
              ) : message.type === 'VIDEO_CIRCLE' && message.mediaFileId ? (
                signedMediaSrc ? (
                  <video
                    src={signedMediaSrc}
                    controls
                    playsInline
                    preload="metadata"
                    className="rounded-2xl max-w-[280px] max-h-[280px] object-cover bg-black"
                    style={{ aspectRatio: '1 / 1' }}
                  />
                ) : (
                  <div className="rounded-2xl w-[200px] h-[200px] bg-surface-800 animate-pulse" />
                )
              ) : message.type === 'VOICE' && message.mediaFileId ? (
                <div className="flex flex-col gap-1.5">
                  {signedMediaSrc ? (
                    <audio
                      src={signedMediaSrc}
                      controls
                      preload="none"
                      className="max-w-full min-w-[200px]"
                    />
                  ) : (
                    <div className="h-10 min-w-[200px] bg-surface-800 rounded animate-pulse" />
                  )}
                  <VoiceTranscript
                    messageId={message.id}
                    transcription={message.transcription ?? null}
                    isOwn={isOwn}
                    onUpdate={(text) => updateChannelMessage({ ...message, transcription: text })}
                  />
                </div>
              ) : (
                <>
                  <p className={clsx(
                    'text-sm leading-relaxed break-words whitespace-pre-wrap',
                    isOwn ? 'text-white' : 'text-surface-100',
                  )}>
                    <StickerShortcodeText text={message.content} />
                  </p>
                  <LinkPreviewInline text={message.content} isOwn={isOwn} />
                </>
              )}

              {/* Footer: reactions (left) + flags + edited + time + read (right) */}
              <div className={clsx(
                'flex items-center gap-1 mt-0.5',
                isOwn ? 'text-white/60' : 'text-surface-500',
              )}>
                {/* Reactions — inline в левом углу футера (Phase 26.2). */}
                <div className="flex-1 min-w-0">
                  {!isDeleted && !editing && (
                    <MessageReactions
                      messageId={isDM ? undefined : message.id}
                      dmId={isDM ? message.id : undefined}
                      initial={message.reactions}
                      align="left"
                      enabled={reactionsEnabled}
                    />
                  )}
                </div>
                {message.silent && <BellOff size={10} className="opacity-70" />}
                {isPinned && <Pin size={10} className="opacity-70" />}
                {isViewOnce && <Eye size={10} className="opacity-70" />}
                {message.editedAt && (
                  <span className="text-[10px]">изм.</span>
                )}
                <span className="text-[10px]">
                  {format(new Date(message.createdAt), 'HH:mm')}
                </span>
                {isOwn && (
                  message.readAt
                    ? <CheckCheck size={12} className="text-sky-400" />
                    : <Check size={12} className="opacity-60" />
                )}
              </div>
            </div>
          )}

          {/* Phase 6 — inline-клавиатура от бота */}
          {!isDeleted && message.botKeyboard && message.botKeyboard.length > 0 && (
            <InlineKeyboard
              keyboard={message.botKeyboard}
              messageId={isDM ? undefined : message.id}
              dmId={isDM ? message.id : undefined}
            />
          )}
        </div>
      </div>

      {forwardOpen && (
        <ForwardModal
          messageId={message.id}
          preview={message.content}
          onClose={() => setForwardOpen(false)}
        />
      )}

      {lightboxOpen && message.mediaFileId && (
        <ImageLightbox
          mediaFileId={message.mediaFileId}
          alt={message.content || 'Изображение'}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      {/* User context menu (left-click on avatar/name of others, right-click on bubble) */}
      {contextMenu && (
        <UserContextMenu
          userId={message.authorId}
          username={message.author.username}
          displayName={message.author.displayName}
          position={contextMenu}
          onClose={() => setContextMenu(null)}
          messageId={message.id}
          channelId={message.channelId || undefined}
        />
      )}

      {/* Telegram-style message actions menu (right-click / long press / click) */}
      {actionsMenu && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/10"
            // pointer-events: none физически выключает hit-test пока действует
            // grace period, иначе любой synthetic click от long-press touchend
            // сразу закроет menu. После 500ms — re-render и snap обратно.
            style={{ pointerEvents: inGrace ? 'none' : 'auto' }}
            onClick={() => setActionsMenu(false)}
            onContextMenu={(e) => { e.preventDefault(); setActionsMenu(false); }}
          />
          <div
            ref={menuRef}
            className="fixed z-50 w-[220px] bg-surface-900 border border-surface-700 rounded-xl shadow-2xl overflow-hidden"
            style={{
              left: menuPos.left,
              top: menuPos.top,
              visibility: menuPos.visibility,
            }}
          >
            {/* Emoji reactions row on top */}
            {reactionsEnabled && !isDeleted && (
              <>
                <div className="px-2 py-2 flex items-center gap-1 overflow-x-auto scrollbar-none bg-surface-850">
                  <ReactionEmojiRow onSelect={async (emoji) => {
                    setActionsMenu(false);
                    try {
                      if (isDM) await reactionsApi.toggleDmReaction(message.id, emoji);
                      else await reactionsApi.toggleChannelReaction(message.id, emoji);
                    } catch {}
                  }} />
                </div>
                <div className="h-px bg-surface-800" />
              </>
            )}
          <div className="py-1.5">
            {!embedMode && selectionScope && !isDeleted && (
              <button onClick={handleEnterSelection}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-200 hover:bg-surface-800">
                <CheckSquare size={13} /> Выделить
              </button>
            )}
            <button onClick={() => { setActionsMenu(false); onReply?.(message); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-200 hover:bg-surface-800">
              <Reply size={13} /> Ответить
            </button>
            {!embedMode && (
              <>
                <button onClick={() => { setActionsMenu(false); setForwardOpen(true); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-200 hover:bg-surface-800">
                  <Forward size={13} /> Переслать
                </button>
                <button onClick={() => { setActionsMenu(false); handleCopy(); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-200 hover:bg-surface-800">
                  <Copy size={13} /> Копировать текст
                </button>
                <button onClick={() => { setActionsMenu(false); handleSaveToPersonal(); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-200 hover:bg-surface-800">
                  <Bookmark size={13} /> В «Сохранённые»
                </button>
                {!isDM && isMod && (
                  <button onClick={() => { setActionsMenu(false); handleTogglePin(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-200 hover:bg-surface-800">
                    {isPinned ? <PinOff size={13} /> : <Pin size={13} />} {isPinned ? 'Открепить' : 'Закрепить'}
                  </button>
                )}
                {isOwn && (
                  <>
                    <div className="h-px bg-surface-800 my-1" />
                    <button onClick={() => { setActionsMenu(false); setEditing(true); setEditContent(message.content); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-200 hover:bg-surface-800">
                      <Edit2 size={13} /> Редактировать
                    </button>
                    {/* Phase 16 — для DM две опции (у меня / у всех),
                        для channel как раньше (одна кнопка «Удалить»). */}
                    {isDM ? (
                      <>
                        <button onClick={() => { setActionsMenu(false); handleDeleteDm('me'); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-200 hover:bg-surface-800">
                          <Trash2 size={13} /> Удалить у меня
                        </button>
                        <button onClick={() => { setActionsMenu(false); handleDeleteDm('all'); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-surface-800">
                          <Trash2 size={13} /> Удалить у всех
                        </button>
                      </>
                    ) : (
                      <button onClick={() => { setActionsMenu(false); handleDelete(); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-surface-800">
                        <Trash2 size={13} /> Удалить
                      </button>
                    )}
                  </>
                )}
                {/* Phase 16 — DM чужое сообщение: можно удалить только у себя. */}
                {!isOwn && isDM && (
                  <>
                    <div className="h-px bg-surface-800 my-1" />
                    <button onClick={() => { setActionsMenu(false); handleDeleteDm('me'); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-200 hover:bg-surface-800">
                      <Trash2 size={13} /> Удалить у меня
                    </button>
                  </>
                )}
                {!isOwn && (
                  <>
                    <div className="h-px bg-surface-800 my-1" />
                    <button onClick={() => {
                      // Open the user-context-menu at our menu's current position
                      setContextMenu({ x: menuPos.left, y: menuPos.top });
                      setActionsMenu(false);
                    }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-200 hover:bg-surface-800">
                      Профиль @{message.author.username}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
          </div>
        </>
      )}
    </>
  );
}

// ── Link Preview ──────────────────────────────────────────────────────────
const URL_REGEX = /https?:\/\/[^\s<>"{}|\\^`[\]]+/g;
const previewCache = new Map<string, { title: string | null; description: string | null; image: string | null; siteName: string | null }>();

function LinkPreviewInline({ text, isOwn }: { text: string; isOwn: boolean }) {
  const urls = text.match(URL_REGEX);
  const url = urls?.[0];
  const [preview, setPreview] = useState<{ title: string | null; description: string | null; image: string | null; siteName: string | null } | null>(null);

  useEffect(() => {
    if (!url) return;
    if (previewCache.has(url)) { setPreview(previewCache.get(url)!); return; }
    let cancelled = false;
    // v5.7.12 — use apiClient (Bearer token auto-attached) + negative caching.
    // Раньше был raw fetch без Authorization header → backend (authenticate-only)
    // отдавал 401 на каждое сообщение с URL'ом → console spam.
    // Теперь: при 401 / empty / no-title → cache null preview, чтобы не повторять.
    apiClient.get(`/api/messages/link-preview`, { params: { url } })
      .then((res) => {
        if (cancelled) return;
        const data = res.data;
        if (data && data.title) {
          previewCache.set(url, data);
          setPreview(data);
        } else {
          // Empty/no-title — кэшируем null, чтобы при следующем mount не дёргать снова.
          previewCache.set(url, { title: null, description: null, image: null, siteName: null });
        }
      })
      .catch((e) => {
        if (cancelled) return;
        // Negative cache на любой error (401/404/network) — больше не пытаемся в этой сессии.
        if (axios.isAxiosError(e)) {
          previewCache.set(url, { title: null, description: null, image: null, siteName: null });
        }
      });
    return () => { cancelled = true; };
  }, [url]);

  if (!preview || !preview.title) return null;

  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
      className={clsx('block mt-1.5 rounded-lg overflow-hidden border', isOwn ? 'border-white/20' : 'border-surface-700')}>
      {preview.image && (
        <img src={preview.image} alt="" className="w-full h-32 object-cover" loading="lazy" />
      )}
      <div className="px-2.5 py-2">
        {preview.siteName && (
          <p className={clsx('text-[10px] uppercase tracking-wider', isOwn ? 'text-white/50' : 'text-surface-500')}>{preview.siteName}</p>
        )}
        <p className={clsx('text-xs font-semibold line-clamp-2', isOwn ? 'text-white' : 'text-surface-200')}>{preview.title}</p>
        {preview.description && (
          <p className={clsx('text-[11px] line-clamp-2 mt-0.5', isOwn ? 'text-white/70' : 'text-surface-400')}>{preview.description}</p>
        )}
      </div>
    </a>
  );
}

// Inline emoji row for Telegram-style action menu
let emojiCache: string[] | null = null;
function ReactionEmojiRow({ onSelect }: { onSelect: (emoji: string) => void }) {
  const [emojis, setEmojis] = useState<string[]>(emojiCache ?? ['👍', '❤️', '😂', '🎉', '😮', '😢', '🔥', '👏']);
  useEffect(() => {
    apiClient.get<Array<{ emoji: string }>>('/api/reactions/available').then((r) => {
      emojiCache = r.data.map((x) => x.emoji);
      setEmojis(emojiCache);
    }).catch(() => {});
  }, []);
  return (
    <>
      {emojis.map((e) => (
        <button
          key={e}
          onClick={() => onSelect(e)}
          className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full hover:bg-surface-700 hover:scale-110 transition-transform text-lg"
        >
          {e}
        </button>
      ))}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// IRC Classic — inline one-liner `[icon] nick: text`
// Left-click on nick  → inject @username into MessageInput via chatStore
// Right-click / long-press on nick → UserContextMenu (profile / DM / friend)
// ────────────────────────────────────────────────────────────────────────────
function ClassicMessageLine({
  message,
  isOwn,
  isDeleted,
  myUsername,
  highlight,
  selectionScope,
}: {
  message: Message;
  isOwn: boolean;
  isDeleted: boolean;
  myUsername: string;
  highlight: boolean;
  selectionScope?: SelectionScope;
}) {
  const injectMention = useChatStore((s) => s.injectMention);
  const mentionHighlightColor = useUiPrefsStore((s) => s.mentionHighlightColor);
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const apiBase = ((import.meta as any).env?.VITE_API_URL ?? '').replace(/\/$/, '');
  const nickColor = (message.author as any).nickColor as string | undefined;
  const nickIcon = (message.author as any).nickIcon as string | undefined;
  const resolvedIcon: string | null = !nickIcon
    ? null
    : /^https?:\/\//i.test(nickIcon)
      ? nickIcon
      : nickIcon.startsWith('/')
        ? `${apiBase}${nickIcon}`
        : null;
  const isUrlIcon = !!resolvedIcon;
  const isEmojiIcon = !!nickIcon && !resolvedIcon;

  const scopeKey = selectionScope?.kind === 'channel'
    ? selectionScope.channelId
    : selectionScope?.kind === 'dm'
      ? selectionScope.userId
      : message.channelId;

  const onNickClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    injectMention(scopeKey, message.author.username);
  };

  const onNickContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setMenuPos({ x: e.clientX, y: e.clientY });
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType !== 'touch') return;
    longPressTimer.current = setTimeout(() => {
      setMenuPos({ x: e.clientX, y: e.clientY });
    }, 500);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  // Parse content for @myUsername highlight
  const mentionParts: Array<{ text: string; mention: boolean }> = [];
  if (myUsername) {
    const re = new RegExp(`@${myUsername}\\b`, 'gi');
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(message.content)) !== null) {
      if (m.index > last) mentionParts.push({ text: message.content.slice(last, m.index), mention: false });
      mentionParts.push({ text: m[0], mention: true });
      last = m.index + m[0].length;
    }
    if (last < message.content.length) mentionParts.push({ text: message.content.slice(last), mention: false });
  } else {
    mentionParts.push({ text: message.content, mention: false });
  }

  return (
    <>
      <div
        data-message-id={message.id}
        className={clsx(
          'px-3 py-0.5 font-mono text-[13px] leading-[18px] hover:bg-surface-900/40 transition-colors',
          highlight && 'bg-brand-500/10',
        )}
      >
        <span
          className="inline-flex items-center align-middle gap-1"
          onContextMenu={onNickContextMenu}
          onPointerDown={onPointerDown}
          onPointerUp={cancelLongPress}
          onPointerCancel={cancelLongPress}
          onPointerLeave={cancelLongPress}
        >
          {isUrlIcon ? (
            <img src={resolvedIcon!} alt="" className="w-3 h-3 rounded-full inline-block" />
          ) : isEmojiIcon ? (
            <span className="text-[11px]">{nickIcon}</span>
          ) : null}
          <button
            type="button"
            onClick={onNickClick}
            className={clsx('font-bold hover:underline cursor-pointer select-none', isOwn && 'underline')}
            style={nickColor ? { color: nickColor } : undefined}
            title="Клик — вставить @упоминание; правый клик — меню"
          >
            {message.author.displayName}
          </button>
        </span>
        <span className="text-surface-300">: </span>
        {isDeleted ? (
          <span className="text-surface-500 italic">(удалено)</span>
        ) : (
          mentionParts.map((p, i) =>
            p.mention ? (
              <span
                key={i}
                className="text-white px-1 rounded font-semibold"
                style={{ background: mentionHighlightColor }}
              >{p.text}</span>
            ) : (
              <span key={i} className="text-surface-100">
                <StickerShortcodeText text={p.text} />
              </span>
            ),
          )
        )}
        {/* v5.0+ — inline-keyboard parity с BUBBLE: бот-кнопки в IRC-режиме
            рендерятся отдельным блоком сразу под текстом сообщения. */}
        {!isDeleted && message.botKeyboard && message.botKeyboard.length > 0 && (
          <div className="ml-6 mt-1 max-w-md">
            <InlineKeyboard
              keyboard={message.botKeyboard}
              messageId={message.channelId ? message.id : undefined}
              dmId={message.channelId ? undefined : message.id}
            />
          </div>
        )}
      </div>

      {menuPos && (
        <UserContextMenu
          userId={message.authorId}
          username={message.author.username}
          displayName={message.author.displayName}
          position={menuPos}
          onClose={() => setMenuPos(null)}
          messageId={message.id}
          channelId={message.channelId || undefined}
        />
      )}
    </>
  );
}

// Phase 26 — parse `:name:` shortcodes and render matching stickers inline.
function StickerShortcodeText({ text }: { text: string }) {
  const byName = useStickerCacheStore((s) => s.byName);
  const ensure = useStickerCacheStore((s) => s.ensure);
  useEffect(() => { void ensure(); }, [ensure]);
  if (!text.includes(':')) return <>{text}</>;
  const re = /:([\w-]+):/g;
  const parts: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const url = byName[m[1]];
    if (url) {
      parts.push(
        <img
          key={`st-${i++}`}
          src={url}
          alt={`:${m[1]}:`}
          className="inline-block align-text-bottom w-6 h-6 object-contain mx-0.5"
          loading="lazy"
        />,
      );
    } else {
      parts.push(m[0]); // literal if unknown
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

export default MessageItem;
